/**
 * 라우트 handle 에 부착하는 화면 타이틀 메타 — **타이틀 SSOT**(페이지는 자체 타이틀을 렌더하지 않는다).
 * 선언 위치는 `app/routing/routes/*.ts` 의 각 라우트 `handle`.
 */
export interface RouteTitleHandle {
  /** 셸 헤더(모바일)·페이지 헤더(데스크탑)·`document.title` 에 쓰이는 화면명. */
  title?: string;
  /**
   * **하위 페이지 여부** — 다른 화면에서 "들어간" 화면인가(발전소 상세·용도설정). 라우트 정의가 화면마다
   * 직접 선언한다(경로 깊이·path variable 파생 금지 — 근거는 `routes/app.ts` 헤더).
   *
   * true 면 '<' 는 히스토리가 아니라 **상위 경로**(현재 pathname 의 마지막 세그먼트 제거)로 올라간다 —
   * `AppHeader` 의 Up 내비게이션. 어느 화면이 하위인지는 사람이 선언하고, 그 상위가 어디인지는 경로가 답한다.
   */
  isSubpage?: boolean;
}

/** [pickRouteTitle] 이 받는 최소 match 형태 — `useMatches()` 결과와 `router.state.matches` 양쪽에 맞는다. */
interface HandleCarrier {
  handle?: unknown;
}

/**
 * 매치 체인에서 **가장 깊은** title 핸들을 고른다. 훅이 아니라 순수 함수 — 트리 안(셸 헤더)과
 * 트리 밖(`document.title` 배선) **양쪽에서 같은 규칙**을 쓰기 위해 여기 둔다.
 *
 * 선택 기준이 `title` 이라, title 없이 `isSubpage` 만 단 handle 은 채택되지 않는다(둘은 같은 handle 에 둘 것).
 */
export function pickRouteTitle(matches: readonly HandleCarrier[]): RouteTitleHandle {
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i]?.handle as RouteTitleHandle | undefined;
    if (handle?.title) return handle;
  }
  return {};
}

/**
 * 매치 체인의 타이틀을 **위에서 아래로 모은다** — `document.title` 의 뎁스 표기용
 * (`헤빛온 · 설비정보 · 발전소명`). 연속 중복은 접는다(부모·자식이 같은 이름을 달고 있는 경우).
 *
 * 화면 하나만 고르는 [pickRouteTitle] 과 용도가 다르다: 헤더는 **현재 화면 하나**를 크게 보여주고,
 * 문서 제목은 **어디에 있는지**를 알려준다. 그래서 규칙을 분리해 둔다.
 */
export function collectRouteTitles(matches: readonly HandleCarrier[]): string[] {
  const titles: string[] = [];
  for (const match of matches) {
    const title = (match?.handle as RouteTitleHandle | undefined)?.title;
    if (title && titles[titles.length - 1] !== title) titles.push(title);
  }
  return titles;
}
